/**
 * ChangePhoneModal — смена номера админа (логина) через
 * POST /admin/clients/:id/admin-phone. Маска +996, живой дедуп номера;
 * код из SMS клиент вводит сам при входе (ADMIN_SPEC §3.5).
 */

import {useState} from 'react';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {Info} from 'lucide-react';

import {changeAdminPhone} from '@services/clients';
import {keys} from '@queries/keys';
import {usePhoneOwner} from '@hooks/usePhoneOwner';
import {Field} from '@components/FormKit';
import {cn} from '@lib/utils';
import {formatKgPhone, phoneDigits} from '@utils/phone';

import {ModalShell} from '../ModalShell';

type Props = {open: boolean; clientId: string; onClose: () => void; onSaved: () => void};

export const ChangePhoneModal = ({open, clientId, onClose, onSaved}: Props) => {
  const queryClient = useQueryClient();
  const [phone, setPhone] = useState('');
  const {owner, checked} = usePhoneOwner(phone);
  const taken = owner !== null;

  const [prevOpen, setPrevOpen] = useState(false);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) setPhone('');
  }

  const mutation = useMutation({
    mutationFn: changeAdminPhone,
    onSuccess: () => {
      void queryClient.invalidateQueries({queryKey: keys.clients.all});
      onSaved();
    },
  });

  const handleSubmit = () => mutation.mutate({id: clientId, newPhone: phone});

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Сменить номер админа"
      subtitle="Номер — логин админа для входа. Клиент войдёт по новому номеру сам, подтвердив вход кодом из SMS."
      submitLabel="Сохранить номер"
      onSubmit={handleSubmit}
      pending={mutation.isPending}
      submitDisabled={phoneDigits(phone).length !== 9 || taken}
      error={mutation.isError ? 'Не удалось сменить номер. Попробуйте ещё раз' : null}
    >
      <Field label="Новый номер телефона">
        <div
          className={cn(
            'flex h-11 items-center gap-2 rounded-[10px] border px-[13px] transition',
            !checked && 'border-input',
            checked && taken && 'border-destructive/40 ring-[3px] ring-skr-danger/10',
            checked && !taken && 'border-success/40 ring-[3px] ring-skr-paid/10',
          )}
        >
          <span className="shrink-0 font-mono text-sm text-muted-foreground">+996</span>
          <input
            value={formatKgPhone(phoneDigits(phone))}
            onChange={(e) => setPhone(phoneDigits(e.target.value))}
            type="tel"
            inputMode="tel"
            placeholder="700 000 000"
            className="flex-1 border-none bg-transparent font-mono text-sm outline-none placeholder:text-muted-foreground"
          />
        </div>
        {checked && (
          <div className={cn('text-xs', taken ? 'text-skr-danger' : 'text-skr-paid')}>
            {taken ? `Этот номер уже привязан к клиенту «${owner}»` : 'Номер свободен'}
          </div>
        )}
      </Field>
      <div className="flex gap-2.5 rounded-[11px] border border-skr-info-border bg-skr-info-chip p-[11px_13px]">
        <Info className="mt-px size-[17px] shrink-0 text-primary" strokeWidth={1.9} />
        <div className="text-xs leading-[1.45] text-skr-info-ink2">
          Код из SMS вводит сам клиент при первом входе — на нашей стороне подтверждение не
          требуется.
        </div>
      </div>
    </ModalShell>
  );
};
